import React, { useState } from "react";
import { toast } from "sonner";
import { Check, Send, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { serviceOptions, frequencyOptions } from "@/data/services";
import api, { formatApiErrorDetail } from "@/lib/api";

const initial = {
  service_type: "",
  frequency: "",
  surface_area: "",
  full_name: "",
  email: "",
  phone: "",
  company: "",
  address: "",
  message: "",
};

export default function Devis() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const step1Valid = form.service_type && form.frequency && Number(form.surface_area) > 0;
  const step2Valid = form.full_name.trim() && form.email.trim() && form.phone.trim() && form.address.trim();

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, surface_area: Number(form.surface_area) };
      const { data } = await api.post("/devis", payload);
      toast.success(data.message || "Demande envoyée");
      setSubmitted(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-24 md:py-32 bg-white">
        <div className="mx-auto max-w-2xl px-6 md:px-10 text-center" data-testid="devis-success">
          <div className="h-20 w-20 rounded-full bg-[#E3F2FD] flex items-center justify-center mx-auto">
            <Check className="h-10 w-10 text-[#2196F3]" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-slate-900 mt-8">
            Demande reçue ! 🎯
          </h1>
          <p className="text-lg text-slate-600 mt-4">
            Merci {form.full_name}. Un de nos conseillers vous recontactera sous 24h ouvrées à l'adresse <strong>{form.email}</strong>.
          </p>
          <Button
            onClick={() => { setSubmitted(false); setForm(initial); setStep(1); }}
            className="mt-8 rounded-full bg-[#2196F3] hover:bg-[#1976D2]"
            data-testid="devis-new-request"
          >
            Nouvelle demande
          </Button>
        </div>
      </section>
    );
  }

  return (
    <div className="bg-white">
      <section className="pt-20 md:pt-28 pb-12 bg-[#f0f7ff]">
        <div className="mx-auto max-w-4xl px-6 md:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2196F3] mb-4">Demande de devis</div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900">
            Obtenez votre devis en <span className="text-[#2196F3]">3 étapes</span>.
          </h1>
          <p className="text-slate-600 mt-4 max-w-xl mx-auto">
            Complétez le formulaire ci-dessous — réponse garantie sous 24 heures ouvrées.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          {/* Stepper */}
          <div className="flex items-center justify-center gap-2 md:gap-6 mb-12">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`h-10 w-10 rounded-full flex items-center justify-center font-display font-bold text-sm ${
                    step >= s ? "bg-[#2196F3] text-white" : "bg-slate-100 text-slate-400"
                  }`}
                  style={{ transitionProperty: "background-color, color", transitionDuration: "300ms" }}
                >
                  {step > s ? <Check className="h-5 w-5" /> : s}
                </div>
                {s < 3 && <div className={`h-px w-8 md:w-14 ${step > s ? "bg-[#2196F3]" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 md:p-12">
            {step === 1 && (
              <div className="space-y-6" data-testid="devis-step-1">
                <div>
                  <h2 className="font-display text-2xl font-bold text-slate-900">Votre prestation</h2>
                  <p className="text-sm text-slate-500 mt-1">Quel type de nettoyage vous intéresse ?</p>
                </div>
                <div className="space-y-2">
                  <Label>Type de prestation *</Label>
                  <Select value={form.service_type} onValueChange={set("service_type")}>
                    <SelectTrigger data-testid="devis-service-type" className="h-12"><SelectValue placeholder="Sélectionnez une prestation" /></SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fréquence souhaitée *</Label>
                  <Select value={form.frequency} onValueChange={set("frequency")}>
                    <SelectTrigger data-testid="devis-frequency" className="h-12"><SelectValue placeholder="Choisissez la fréquence" /></SelectTrigger>
                    <SelectContent>
                      {frequencyOptions.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surface">Superficie estimée (m²) *</Label>
                  <Input id="surface" type="number" min={1} value={form.surface_area} onChange={set("surface_area")} placeholder="Ex : 250" className="h-12" data-testid="devis-surface" />
                </div>
                <div className="flex justify-end pt-4">
                  <Button onClick={() => setStep(2)} disabled={!step1Valid} className="rounded-full bg-[#2196F3] hover:bg-[#1976D2] h-12 px-8" data-testid="devis-next-1">
                    Étape suivante <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6" data-testid="devis-step-2">
                <div>
                  <h2 className="font-display text-2xl font-bold text-slate-900">Vos coordonnées</h2>
                  <p className="text-sm text-slate-500 mt-1">Pour vous recontacter avec une proposition adaptée.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fname">Nom complet *</Label>
                    <Input id="fname" value={form.full_name} onChange={set("full_name")} placeholder="Nom et prénom" className="h-12" data-testid="devis-fullname" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Société</Label>
                    <Input id="company" value={form.company} onChange={set("company")} placeholder="Nom de l'entreprise" className="h-12" data-testid="devis-company" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={form.email} onChange={set("email")} placeholder="vous@email.fr" className="h-12" data-testid="devis-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input id="phone" value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" className="h-12" data-testid="devis-phone" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="addr">Adresse du site *</Label>
                  <Input id="addr" value={form.address} onChange={set("address")} placeholder="Adresse complète du lieu à nettoyer" className="h-12" data-testid="devis-address" />
                </div>
                <div className="flex justify-between pt-4">
                  <Button onClick={() => setStep(1)} variant="outline" className="rounded-full h-12 px-6" data-testid="devis-prev-2">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Retour
                  </Button>
                  <Button onClick={() => setStep(3)} disabled={!step2Valid} className="rounded-full bg-[#2196F3] hover:bg-[#1976D2] h-12 px-8" data-testid="devis-next-2">
                    Étape suivante <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6" data-testid="devis-step-3">
                <div>
                  <h2 className="font-display text-2xl font-bold text-slate-900">Précisions</h2>
                  <p className="text-sm text-slate-500 mt-1">Décrivez votre besoin (facultatif).</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="msg">Votre message</Label>
                  <Textarea id="msg" rows={6} value={form.message} onChange={set("message")} placeholder="Détails sur les horaires, contraintes d'accès, besoins spécifiques…" data-testid="devis-message" />
                </div>
                <div className="bg-slate-50 rounded-xl p-5 text-sm text-slate-600 space-y-1">
                  <div className="font-semibold text-slate-900 mb-2">Récapitulatif</div>
                  <div>• Prestation : <strong>{form.service_type}</strong></div>
                  <div>• Fréquence : <strong>{form.frequency}</strong></div>
                  <div>• Superficie : <strong>{form.surface_area} m²</strong></div>
                  <div>• Demandeur : <strong>{form.full_name}</strong> — {form.email}</div>
                </div>
                <div className="flex justify-between pt-4">
                  <Button onClick={() => setStep(2)} variant="outline" className="rounded-full h-12 px-6" data-testid="devis-prev-3">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Retour
                  </Button>
                  <Button onClick={submit} disabled={submitting} className="rounded-full bg-[#2196F3] hover:bg-[#1976D2] h-12 px-8" data-testid="devis-submit">
                    {submitting ? "Envoi…" : (<>Envoyer ma demande <Send className="ml-2 h-4 w-4" /></>)}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
